/**
 * Project switcher thumbnails — capture side and read side.
 *
 * Capture: after a cloud save, the homepage is rebuilt through the saved-preview
 * bundler (self-contained HTML, assets inlined) and handed to the Worker, which
 * renders it via Browserless. That work is expensive on both ends, so it is
 * throttled three ways: an in-memory per-project attempt clock, the server's own
 * freshness window, and idle scheduling so it never competes with the save.
 *
 * Read: thumbnails live behind an access-checked route, so an <img src> cannot
 * fetch them directly (no cookie/bearer on image requests to another origin).
 * Callers ask for an object URL and release the cache when their UI closes.
 */

import { reportError } from '../core/report-error.js';

/** Matches the server-side cooldown (THUMB_MIN_INTERVAL_MS). */
export const THUMB_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

/** Skip capture rather than upload an enormous bundle (server caps at 12 MB). */
const MAX_THUMB_SOURCE_BYTES = 10 * 1024 * 1024;

/** Give the save's own network + IDB work room to finish first. */
const CAPTURE_DELAY_MS = 2500;

/** projectId → epoch ms of the last capture attempt from this tab. */
const lastAttemptAt = new Map();

/** projectId → { url, thumbUpdatedAt } object URLs owned by this module. */
const objectUrls = new Map();

/** projectId → in-flight fetch, so a re-render doesn't double-download. */
const inflightReads = new Map();

/**
 * Set once the Worker reports that capture is unconfigured (no Browserless key,
 * i.e. every local-dev session). Stops us rebuilding preview bundles for nothing.
 */
let captureUnavailable = false;

function schedule(fn) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => fn(), { timeout: 10_000 });
    return;
  }
  setTimeout(fn, 0);
}

/**
 * Fire-and-forget refresh after a successful cloud save. Never throws, never
 * blocks the caller, and quietly no-ops when the capture would be wasted.
 *
 * @param {string} projectId
 * @param {{thumbUpdatedAt?:number, force?:boolean}} [opts]
 *   thumbUpdatedAt — the server's last capture time from the save response
 */
export function scheduleThumbnailCapture(projectId, { thumbUpdatedAt = null, force = false } = {}) {
  const id = String(projectId || '');
  if (!id || captureUnavailable) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const now = Date.now();
  if (!force) {
    const attempted = lastAttemptAt.get(id);
    if (Number.isFinite(attempted) && now - attempted < THUMB_REFRESH_INTERVAL_MS) return;
    if (
      Number.isFinite(Number(thumbUpdatedAt)) &&
      now - Number(thumbUpdatedAt) < THUMB_REFRESH_INTERVAL_MS
    )
      return;
  }
  // Claim the slot before the async work so a burst of saves captures once.
  lastAttemptAt.set(id, now);

  setTimeout(
    () =>
      schedule(() => {
        captureAndUpload(id, force).catch((err) => {
          reportError('Project thumbnail', err, {
            user: 'silent',
            detail: 'thumbnail capture failed'
          });
        });
      }),
    CAPTURE_DELAY_MS
  );
}

async function captureAndUpload(projectId, force) {
  // Lazy: the bundler pulls the whole export pipeline, and the auth client
  // pulls the template API. Neither belongs on the save path's critical import
  // graph (or in the pure save/export test contexts).
  const [{ buildPreviewBundle }, { putProjectThumbnail }] = await Promise.all([
    import('./preview-bundler.js'),
    import('../templates/auth-api.js')
  ]);

  let bundle;
  try {
    bundle = await buildPreviewBundle();
  } catch (err) {
    // Oversized or unbuildable projects simply keep their monogram tile.
    reportError('Project thumbnail', err, {
      user: 'silent',
      detail: 'could not build preview bundle for the thumbnail'
    });
    return;
  }

  const html = bundle?.html || '';
  if (!html || new Blob([html]).size > MAX_THUMB_SOURCE_BYTES) return;

  try {
    await putProjectThumbnail(projectId, html, { force });
  } catch (err) {
    if (err?.data?.code === 'thumbnails_unavailable' || err?.status === 503) {
      captureUnavailable = true;
      return;
    }
    throw err;
  }
}

/**
 * Object URL for a project's stored thumbnail, or null when there is no
 * capture yet. Cached per project; `thumbUpdatedAt` busts the cache so a fresh
 * capture replaces a stale image without a full reload.
 *
 * @param {string} projectId
 * @param {{thumbUpdatedAt?:number}} [opts]
 * @returns {Promise<string|null>}
 */
export async function loadThumbnailObjectUrl(
  projectId,
  { thumbUpdatedAt = null, signal = null } = {}
) {
  const id = String(projectId || '');
  if (!id) return null;

  const cached = objectUrls.get(id);
  if (cached && cached.thumbUpdatedAt === thumbUpdatedAt) return cached.url;

  const pending = signal ? null : inflightReads.get(id);
  if (pending) return pending;

  const read = (async () => {
    const { getProjectThumbnail } = await import('../templates/auth-api.js');
    const blob = await getProjectThumbnail(id, { signal });
    if (!blob || !blob.size) return null;
    if (signal?.aborted) return null;
    // A newer capture supersedes whatever this project held before.
    releaseThumbnail(id);
    const url = URL.createObjectURL(blob);
    if (signal?.aborted) {
      URL.revokeObjectURL(url);
      return null;
    }
    objectUrls.set(id, { url, thumbUpdatedAt });
    return url;
  })()
    .catch((err) => {
      reportError('Project thumbnail', err, {
        user: 'silent',
        detail: 'thumbnail fetch failed'
      });
      return null;
    })
    .finally(() => {
      inflightReads.delete(id);
    });

  if (!signal) inflightReads.set(id, read);
  return read;
}

/** Revoke one project's object URL. */
export function releaseThumbnail(projectId) {
  const entry = objectUrls.get(String(projectId || ''));
  if (!entry) return;
  URL.revokeObjectURL(entry.url);
  objectUrls.delete(String(projectId || ''));
}

/** Revoke every object URL this module owns (call when the switcher closes). */
export function releaseAllThumbnails() {
  for (const [, entry] of objectUrls) URL.revokeObjectURL(entry.url);
  objectUrls.clear();
}

/** Test seam — clears throttles, cached URLs, and the unavailable latch. */
export function _resetThumbnailState() {
  releaseAllThumbnails();
  lastAttemptAt.clear();
  inflightReads.clear();
  captureUnavailable = false;
}
